import { AfterViewInit, ChangeDetectorRef, Component, Inject, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Observable, OperatorFunction } from 'rxjs';

// services
import { Utils } from '../../services/utils.service';
import { EquipmentBase } from '../../../planogram/models/equipment-base';
import { POSMaterial } from '../../../planogram/models/POSMaterials/POSMaterial';
import { PlanogramComponent } from '../../../planogram/components/planogram/planogram.component';
import { EquipmentModelComponent } from '../../components/equipment-model/equipment-model.component';
import { ProductGroup } from '../../../planogram/models/product-group';

export enum AdditionalParams {
	AsSingleFile = 'as_single_file',
	GroupByPlans = 'group_by_plans',
	SkipImages = 'skip_images',
	AlbumView = 'landscape',
	ExcludeMasterPositions = 'exclude_mp_relations'
}

export type PlanogramFileView = '2d' | '3d';

export interface IInputEquipmentsModalData {
	count?: number;
	title?: string;
	params?: Object;
	downloadMethod: (params: Object) => Observable<void>;
	pipeMethod?: OperatorFunction<any, any>,
	additionalParams?: Array<IEquipmentDownloadAdditionalParam>;
	showPlanogramViewSelector?: boolean;
	hasMasterPositions?: boolean;
	previewStand?: EquipmentBase;
	previewPhotos?: boolean;
	previewPOSMaterials?: Array<POSMaterial>;
}

export interface IEquipmentDownloadAdditionalParam {
	param: AdditionalParams;
	title: string;
	type: 'toggle' | 'icons';
	icons?: [{ icon: string; title?: string }, { icon: string; title?: string }];
	info?: string;
}

export const STAND_COMPLETION_ADDITIONAL_PARAMS: Array<IEquipmentDownloadAdditionalParam> = [
	{
		param: AdditionalParams.ExcludeMasterPositions,
		title: 'MASTER_POSITION.DONT_INCLUDES_PRODUCTS',
		type: 'toggle',
		info: 'MASTER_POSITION.INCLUDE_PRODUCTS_COMPLETION_HINT'
	},
	{
		param: AdditionalParams.AlbumView,
		title: 'COMMON.COMPLETION_VIEW_SELECT',
		type: 'icons',
		icons: [
			{ icon: 'viewBook', title: 'COMMON.BOOK_VIEW' },
			{ icon: 'viewAlbum', title: 'COMMON.ALBUM_VIEW' }
		]
	}
];

type AdditionalFormParams = {
	[key in AdditionalParams]: FormControl<boolean>;
}

interface IFormParams extends Partial<AdditionalFormParams> {
	equipment_format: FormControl<FileFormat>;
	planogram_view: FormControl<PlanogramFileView>;
}

type AdditionalRequestParams = {
	[key in AdditionalParams]: boolean;
}

interface IRequestParams extends Partial<AdditionalRequestParams> {
	equipment_format: FileFormat;
	planogram_view?: PlanogramFileView;
	image?: string;
}

export interface IMultiEquipmentsModalResult extends Partial<AdditionalRequestParams> {
	format: FileFormat;
	count: number;
	planogram_view?: PlanogramFileView;
}

export type FileFormat = 'pdf' | 'excel'

@Component({
	selector: 'app-equipments-download-modal',
	templateUrl: './equipments-download-modal.component.html',
	styleUrls: ['./equipments-download.component.scss'],
	standalone: false
})
export class EquipmentsDownloadModalComponent implements OnInit, AfterViewInit {

	inputData: IInputEquipmentsModalData;
	formGroup: FormGroup<IFormParams>;
	previewData: { id: number | undefined; standData: Record<string, unknown> };
	visibleAdditionalParams: Array<IEquipmentDownloadAdditionalParam> = [];
	isDownloading = false;
	isPreview3dLoading = false;
	readonly additionalParams: typeof AdditionalParams = AdditionalParams;
	@ViewChild(PlanogramComponent) previewPlanogram?: PlanogramComponent;
	@ViewChild(EquipmentModelComponent) previewEquipmentModel?: EquipmentModelComponent;

	constructor(
		private dialogRef: MatDialogRef<EquipmentsDownloadModalComponent>,
		@Inject(MAT_DIALOG_DATA) private data: IInputEquipmentsModalData,
		private cdr: ChangeDetectorRef
	) {}

	ngOnInit(): void {
		this.inputData = this.data;
		this.previewData = { id: this.inputData?.previewStand?.id, standData: {} };
		this.formGroup = new FormGroup<IFormParams>({
			equipment_format: new FormControl<FileFormat>('excel'),
			planogram_view: new FormControl<PlanogramFileView>('2d')
		});

		this.inputData?.additionalParams?.forEach((param: IEquipmentDownloadAdditionalParam): void => {
			this.formGroup.addControl(param.param, new FormControl<boolean>(this.getInitialParamValue(param.param)));
		});

		this.visibleAdditionalParams = this.buildVisibleAdditionalParams();
		if (this.shouldShowPlanogramViewSelector()) {
			this.isPreview3dLoading = true;
		}
		this.formGroup.controls.equipment_format.valueChanges.subscribe((format: FileFormat): void => {
			this.updateControlsState(format);
		});
		this.updateControlsState(this.formGroup.controls.equipment_format.value);
	}

	ngAfterViewInit(): void {
		if (!this.shouldShowPlanogramViewSelector()) return;

		void this.initializePreview().finally((): void => {
			this.isPreview3dLoading = false;
			this.cdr.detectChanges();
		});
	}

	private async initializePreview(): Promise<void> {
		this.previewPlanogram?.canvasResize();
		await this.preloadPreviewProductImages();
		await this.initPreviewEquipmentModel();
		this.previewPlanogram?.canvasResize();
	}

	private async initPreviewEquipmentModel(): Promise<void> {
		if (!this.previewEquipmentModel) return;

		if (this.previewEquipmentModel.inited) {
			await this.previewEquipmentModel.updateScene();
		} else {
			this.previewEquipmentModel.init();
			await this.previewEquipmentModel.updateScene();
		}

		this.previewEquipmentModel.render();
		await this.waitForPreviewRenderFrame();
	}

	private waitForPreviewRenderFrame(): Promise<void> {
		return new Promise((resolve: () => void): void => {
			requestAnimationFrame((): void => {
				requestAnimationFrame((): void => resolve());
			});
		});
	}

	hasAdditionalParam(paramName: AdditionalParams): boolean {
		return !!this.inputData?.additionalParams?.some((param: IEquipmentDownloadAdditionalParam): boolean => (
			param.param === paramName
		));
	}

	shouldShowPlanogramViewSelector(): boolean {
		return !!this.inputData?.showPlanogramViewSelector && !!this.inputData?.previewStand;
	}

	selectPlanogramView(view: PlanogramFileView): void {
		this.formGroup.controls.planogram_view.setValue(view);
	}

	isExcelFormat(): boolean {
		return this.formGroup.controls.equipment_format.value === 'excel';
	}

	isParamDisabled(paramName: AdditionalParams): boolean {
		return paramName === AdditionalParams.GroupByPlans && !this.isExcelFormat();
	}

	getParamTitle(param: IEquipmentDownloadAdditionalParam): string {
		if (param.param === AdditionalParams.SkipImages) return 'STAND_EQUIPMENTS.DONT_INCLUDE_IMAGES';
		if (param.param === AdditionalParams.ExcludeMasterPositions) return 'MASTER_POSITION.DONT_INCLUDES_PRODUCTS';

		return param.title;
	}

	getTitle(): string {
		return this.inputData?.title ?? 'STAND_EQUIPMENTS.DOWNLOAD_PLAN_COMPLETATIONS_TITLE';
	}

	isDownloadDisabled(): boolean {
		return this.isDownloading
			|| !this.formGroup.controls.equipment_format.value
			|| (this.shouldShowPlanogramViewSelector() && this.isPreview3dLoading);
	}

	download(): void {
		if (this.isDownloadDisabled()) return;

		this.isDownloading = true;

		if (this.inputData.downloadMethod) {
			const params: IRequestParams = this.buildRequestParams();

			let request$: Observable<void> = this.inputData.downloadMethod(params);
			if (this.inputData.pipeMethod) {
				request$ = request$.pipe(this.inputData.pipeMethod);
			}
			request$.subscribe();
		}
		this.close(true);
	}

	close(isDownload: boolean = false): void {
		let result: IMultiEquipmentsModalResult | null = null;
		if (isDownload) {
			result = {
				format: this.formGroup.controls.equipment_format.value,
				count: this.inputData.count
			}
			if (this.shouldShowPlanogramViewSelector()) {
				result.planogram_view = this.formGroup.controls.planogram_view.value;
			}
			this.inputData?.additionalParams?.forEach((param: IEquipmentDownloadAdditionalParam): void => {
				if (!this.shouldShowAdditionalParam(param.param)) return;
				result[param.param] = this.getRequestParamValue(param.param);
			});
		}
		this.dialogRef.close(result);
	}

	private getInitialParamValue(paramName: AdditionalParams): boolean {
		return paramName === AdditionalParams.SkipImages || paramName === AdditionalParams.ExcludeMasterPositions;
	}

	private shouldShowAdditionalParam(paramName: AdditionalParams): boolean {
		return paramName !== AdditionalParams.ExcludeMasterPositions || this.inputData?.hasMasterPositions !== false;
	}

	private getRequestParamValue(paramName: AdditionalParams): boolean {
		const value: boolean = this.formGroup.get(paramName).value;

		if (paramName === AdditionalParams.GroupByPlans && !this.isExcelFormat()) return false;
		if (paramName === AdditionalParams.SkipImages || paramName === AdditionalParams.ExcludeMasterPositions) return !value;

		return value;
	}

	private updateControlsState(format: FileFormat): void {
		const albumViewControl: AbstractControl<boolean, boolean> = this.formGroup.get(AdditionalParams.AlbumView);
		const groupByPlansControl: AbstractControl<boolean, boolean> = this.formGroup.get(AdditionalParams.GroupByPlans);

		if (format === 'excel') {
			albumViewControl?.setValue(false, { emitEvent: false });
			albumViewControl?.disable({ emitEvent: false });
			groupByPlansControl?.enable({ emitEvent: false });

			return;
		}

		albumViewControl?.enable({ emitEvent: false });
		groupByPlansControl?.setValue(false, { emitEvent: false });
		groupByPlansControl?.disable({ emitEvent: false });
	}

	private preloadPreviewProductImages(): Promise<void> {
		const products: Array<ProductGroup> = this.inputData?.previewStand?.products ?? [];

		return Promise.all(products.map((productGroup: ProductGroup) => this.waitForProductImage(productGroup)))
			.then((): void => undefined);
	}

	private waitForProductImage(productGroup: ProductGroup): Promise<void> {
		return new Promise((resolve: () => void): void => {
			const product = productGroup?.product;

			if (!product) {
				resolve();
				return;
			}

			product.loadImage();
			const image: HTMLImageElement | undefined = product.image;

			if (!image?.src || image.complete) {
				resolve();
				return;
			}

			image.onload = (): void => resolve();
			image.onerror = (): void => resolve();
		});
	}

	private buildRequestParams(): IRequestParams {
		const params: IRequestParams = {
			equipment_format: this.formGroup.controls.equipment_format.value
		};

		Utils.assignObject(params, this.inputData.params);

		if (this.shouldShowPlanogramViewSelector()) {
			params.planogram_view = this.formGroup.controls.planogram_view.value;
			this.assign3DPreviewState(params);
		}

		this.inputData?.additionalParams?.forEach((param: IEquipmentDownloadAdditionalParam): void => {
			if (!this.shouldShowAdditionalParam(param.param)) return;
			params[param.param] = this.getRequestParamValue(param.param);
		});

		return params;
	}

	private buildVisibleAdditionalParams(): Array<IEquipmentDownloadAdditionalParam> {
		return this.inputData?.additionalParams?.filter((param: IEquipmentDownloadAdditionalParam): boolean => (
			param.param !== AdditionalParams.AlbumView && this.shouldShowAdditionalParam(param.param)
		)) ?? [];
	}

	private assign3DPreviewState(params: IRequestParams): void {
		if (params.planogram_view !== '3d') return;

		const format = this.formGroup.controls.equipment_format.value;
		const fromCameraView = format === 'pdf' || format === 'excel';
		const cropToContent = format === 'pdf';
		const modelImage: string | null = this.previewEquipmentModel?.getExportImage?.(fromCameraView, cropToContent) ?? null;

		if (modelImage) {
			params.image = modelImage;
		}
	}
}